import React from 'react';
import styled from 'styled-components';

export interface SeenByDialogEntry {
  userId: string;
  fullName: string;
  avatar?: string | null;
  lastSeenAt: string;
}

type SeenByDialogProps = {
  open: boolean;
  title: string;
  items: SeenByDialogEntry[];
  loading?: boolean;
  emptyMessage?: string;
  onClose: () => void;
};

const formatSeenAt = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Seen recently';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const initialsForName = (name: string) =>
  name
    .split(/\s+/)
    .map((part) => part.trim().charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

export const SeenByDialog: React.FC<SeenByDialogProps> = ({
  open,
  title,
  items,
  loading = false,
  emptyMessage = 'No viewers yet.',
  onClose,
}) => {
  if (!open) return null;

  return (
    <Overlay onClick={onClose}>
      <DialogCard
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <CloseButton type="button" aria-label="Close seen by dialog" onClick={onClose}>
            ×
          </CloseButton>
        </DialogHeader>
        {loading ? <StateText>Loading viewers...</StateText> : null}
        {!loading && items.length === 0 ? <StateText>{emptyMessage}</StateText> : null}
        {!loading && items.length > 0 ? (
          <List>
            {items.map((item) => (
              <ListItem key={`${item.userId}-${item.lastSeenAt}`}>
                {item.avatar ? (
                  <AvatarImage src={item.avatar} alt={item.fullName} />
                ) : (
                  <AvatarFallback aria-hidden="true">{initialsForName(item.fullName || 'CM')}</AvatarFallback>
                )}
                <ListBody>
                  <ListName>{item.fullName || 'Community Member'}</ListName>
                  <ListMeta>{formatSeenAt(item.lastSeenAt)}</ListMeta>
                </ListBody>
              </ListItem>
            ))}
          </List>
        ) : null}
      </DialogCard>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.56);
  padding: 1.5rem;
`;

const DialogCard = styled.div`
  width: min(32rem, 100%);
  max-height: min(36rem, calc(100vh - 3rem));
  overflow: auto;
  border-radius: 1rem;
  background: #ffffff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18);
  padding: 1.1rem;
`;

const DialogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.9rem;
`;

const DialogTitle = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: #475569;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
`;

const StateText = styled.p`
  margin: 0;
  color: #475569;
  font-size: 0.95rem;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.9rem;
  padding: 0.8rem;
`;

const ListBody = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const ListName = styled.div`
  color: #0f172a;
  font-size: 0.95rem;
  font-weight: 600;
`;

const ListMeta = styled.div`
  color: #64748b;
  font-size: 0.85rem;
`;

const AvatarImage = styled.img`
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 999px;
  object-fit: cover;
`;

const AvatarFallback = styled.div`
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 0.8rem;
  font-weight: 700;
`;
